<template>
  <page>
    <div :load="doInit">
      <v-expansion-panels v-model="panel1" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div><v-icon>mdi-filter-variant</v-icon>Date Range</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card>
              <v-card-text>
                <v-form>
                  <v-container fluid grid-list-sm>
                    <v-layout row wrap>
                      <v-flex xs6>
                        <cal-picker
                          v-model="reportForm.tranDateFrom"
                          :label="labels.tranDateFrom"
                          v-validate="fromDateValidation(reportForm.tranDateTo)"
                          :data-vv-as="labels.tranDateFrom"
                          data-vv-name="tranDateFrom"
                          :error-messages="errors.collect('tranDateFrom')"
                        ></cal-picker>
                      </v-flex>
                      <v-flex xs6>
                        <cal-picker
                          v-model="reportForm.tranDateTo"
                          :label="labels.tranDateTo"
                          v-validate="toDateValidation(reportForm.tranDateFrom)"
                          :data-vv-as="labels.tranDateTo"
                          data-vv-name="tranDateTo"
                          :error-messages="errors.collect('tranDateTo')"
                          :min="reportForm.tranFromData"
                          :max="today"
                        ></cal-picker>
                      </v-flex>
                    </v-layout>
                  </v-container>
                </v-form>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-spacer class="mt-2"></v-spacer>
      <v-expansion-panels v-model="panel2" multiple>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div><v-icon>mdi-filter-variant</v-icon> Selection Criteria</div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card>
              <v-card-text>
                <v-container fluid grid-list-sm>
                  <v-layout row wrap>
                    <!-- Account No.(s) -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="reportForm.acctNo"
                        :label="labels.acctNo"
                        :error-messages="errors.collect('acctNo')"
                        :data-vv-as="labels.acctNo"
                        data-vv-name="acctNo"
                        append-icon="mdi-magnify"
                        @click:append="multiInterHelpList = true"
                        readonly
                      ></v-text-field>
                    </v-flex>
                    <!-- Insured(s) -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="reportForm.insured"
                        :label="labels.insured"
                        :error-messages="errors.collect('insured')"
                        :data-vv-as="labels.insured"
                        data-vv-name="insured"
                        append-icon="mdi-magnify"
                        @click:append="multiInsuredHelpList = true"
                        readonly
                      ></v-text-field>
                    </v-flex>
                    <!-- Policy Type(s) -->
                    <v-flex xs12>
                      <v-text-field
                        v-model="reportForm.polType"
                        :label="labels.polType"
                        :error-messages="errors.collect('polType')"
                        :data-vv-as="labels.polType"
                        data-vv-name="polType"
                        append-icon="mdi-magnify"
                        @click:append="multiPolTypeHelpList = true"
                        readonly
                      ></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-spacer class="mt-2"></v-spacer>
      <v-form>
        <v-container fluid grid-list-sm>
          <v-layout row wrap>
            <v-flex xs12 sm12 md12 lg12>
              <v-checkbox dense v-model="reportForm.printRptRunTime" :label="labels.printRptRunTime" data-vv-name="printRptRunTime" :data-vv-as="labels.printRptRunTime"></v-checkbox>
            </v-flex>
            <v-flex lg6 xs12>
              <v-btn class="mr-2" color="primary" type="submit" :loading="loading" @click.prevent="doExport">
                <v-icon left dark>mdi-file-export</v-icon>
                {{ labels.export }}
              </v-btn>
              <v-btn-toggle dense mandatory v-model="reportForm.format">
                <v-btn text v-for="format in supportedList" :key="format.value" :value="format.value">
                  <span>{{ format.label }}</span>
                  <v-icon :color="format.color">{{ format.icon }}</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>
        </v-container>
      </v-form>
    </div>
    <!-- Account Helplist -->
    <v-dialog v-model="multiInterHelpList" width="900px" persistent>
      <multi-intermediary-help-list
        :intr="reportForm.acctNo"
        :intrGrp="intrGrp"
        :listName="'billAcctNoList'"
        helpListTitle="Billing Account No. Helplist"
        @close="doClose"
        @doAssign="doAssign('acctNo', $event)"
      ></multi-intermediary-help-list>
    </v-dialog>
    <!-- Insured Helplist -->
    <v-dialog v-model="multiInsuredHelpList" width="900px" persistent>
      <multi-insured-help-list :insdRef="reportForm.insured" @close="doClose" @doAssign="doAssign('insured', $event)"></multi-insured-help-list>
    </v-dialog>
    <!-- Policy Type Helplist -->
    <v-dialog v-model="multiPolTypeHelpList" width="900px" persistent>
      <multi-pol-type-help-list :pt="reportForm.polType" @close="doClose" @doAssign="doAssign('polType', $event)"></multi-pol-type-help-list>
    </v-dialog>
  </page>
</template>

<script>
import { extractMenu } from '@/utils/extract'
import { reportFormat } from '@/const/report'
import MessageMixin from '@/mixins/message'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { checkDateRangeFrom, checkDateRangeTo } from '@/directives/common'
import MultiIntermediaryHelpList from '@/components/core/common/dialog/MultiIntermediaryHelpList.vue'
import MultiInsuredHelpList from '@/components/core/common/dialog/MultiInsuredHelpList.vue'
import MultiPolTypeHelpList from '@/components/core/common/dialog/MultiPolTypeHelpList.vue'

export default {
  data() {
    return {
      labels: {
        dateRange: 'Date Ranges',
        tranDateFrom: 'Transaction Date From*',
        tranDateTo: 'Transaction Date To*',
        acctNo: 'Account No.(s)',
        insured: 'Insured(s)',
        polType: 'Policy Type(s)',
        format: 'Export Format',
        export: 'Export to',
        printRptRunTime: 'Print Runtime Parameters'
      },
      reportForm: {
        tranDateFrom: undefined,
        tranDateTo: undefined,
        acctNo: 'All',
        insured: 'All',
        polType: 'All',
        format: undefined,
        printRptRunTime: true
      },
      // HelpList
      multiInterHelpList: false,
      multiInsuredHelpList: false,
      multiPolTypeHelpList: false,
      // Other
      today: new Date(),
      menu: {},
      supportedFormat: [],
      loading: false,
      intrGrp: '10,11,25',
      panel1: [0],
      panel2: [0]
    }
  },
  computed: {
    rptMenu() {
      return this.$store.state.reports.uwReports.menu
    },
    supportedList() {
      const result = []
      if (this.supportedFormat && this.supportedFormat.length > 0) {
        this.supportedFormat.map(format => {
          if (reportFormat[format]) {
            result.push(reportFormat[format])
          }
        })
      }
      return result
    }
  },
  methods: {
    async doInit() {
      try {
        if (this.$store.state.reports.uwReports.menu !== 1) {
          await this.$store.dispatch('uwReportsInit')
        }
        const apiURI = this.rptMenu.initMarineList
        const result = await this.$http.get(apiURI)
        if (result.data) {
          const { data } = result
          if (data) {
            this.supportedFormat = data.data.format
            this.menu = extractMenu(result.data._links)
          }
        }
      } catch (err) {
        throw err
      }
    },
    doAssign(type, obj) {
      switch (type) {
        case 'acctNo':
          this.reportForm.acctNo = []
          this.reportForm.acctNo = obj.finalSelectedItems.toString()
          break
        case 'insured':
          this.reportForm.insured = []
          this.reportForm.insured = obj.finalSelectedItems.toString()
          break
        case 'polType':
          this.reportForm.polType = []
          this.reportForm.polType = obj.finalSelectedItems.toString()
          break
      }
    },
    doClose() {
      this.multiInterHelpList = false
      this.multiInsuredHelpList = false
      this.multiPolTypeHelpList = false
    },
    doExport() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          this.processExport()
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async processExport() {
      const apiURI = this.menu.exportMarineList
      if (apiURI) {
        try {
          this.loading = true
          await this.$http.post(apiURI, this.reportForm)
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    fromDateValidation(toDate1) {
      if (toDate1) {
        return 'required|checkDateRangeFrom:' + toDate1
      } else {
        return 'required|checkDateRangeFrom'
      }
    },
    toDateValidation(fromDate1) {
      if (fromDate1) {
        return 'required|checkDateRangeTo:' + fromDate1
      } else {
        return 'required|checkDateRangeTo'
      }
    }
  },
  created() {
    this.doInit()
    this.$validator.extend('checkDateRangeFrom', checkDateRangeFrom)
    this.$validator.extend('checkDateRangeTo', checkDateRangeTo)
  },
  watch: {},
  mixins: [MessageMixin, commonAjaxMixin],
  components: {
    MultiIntermediaryHelpList,
    MultiInsuredHelpList,
    MultiPolTypeHelpList
  }
}
</script>
